import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChatRailFareTicket,
  ChatRailResultCard,
  ChatRailReturnOption,
  ChatRailReturnPrompt,
  RuleActionExtension
} from '@sabstravtech/obtservices/base';
import { ChatbotService, ResultAwareService } from '@sabstravtech/obtservices/angular';
import { formatCardCurrency, policyStatusClass, policyStatusLabel } from '../chat-card.util';

@Component({
  selector: 'app-chat-rail-card',
  standalone: false,
  templateUrl: './chat-rail-card.component.html',
  styleUrls: ['./chat-rail-card.component.scss']
})
export class ChatRailCardComponent implements OnInit, OnChanges {
  @Input() card!: ChatRailResultCard;
  @Input() isReturn = false;
  @Input() isEuRail = false;
  /** ENT-16247 — id of the assistant message these results belong to. */
  @Input() feedbackBaseId = '';
  /** ENT-18234 — the return-leg options to collect, set only on the card whose
   *  fare was clicked. Null on every other card and once the pair is complete. */
  @Input() returnPrompt: ChatRailReturnPrompt | null = null;

  readonly fareOptionsLabel = $localize`:@@chatRailFareOptionsAria:View fare options for`;

  isExpanded = false;
  /** Which fare section's full ticket list is being shown: 'single' | 'return' | null */
  expandedSection: 'single' | 'return' | null = null;

  /** ENT-18234 - index of the return train whose fares are open, null when all are collapsed. */
  expandedReturnIndex: number | null = null;

  operatorBadgeClass = 'rail-card__operator-badge--default';

  constructor(
    private resultAwareService: ResultAwareService,
    private chatbotService: ChatbotService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.operatorBadgeClass = this.resolveOperatorBadgeClass();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['returnPrompt'] && this.returnPrompt) {
      this.expandedReturnIndex = null;
      this.scrollReturnPromptIntoView();
    }
  }

  /** ENT-16247 — synthetic feedback id, unique per leg + card. */
  get feedbackId(): string {
    const leg = this.isReturn ? 'in' : 'out';
    return `${this.feedbackBaseId || 'card'}::rail-${leg}-${this.card.index}`;
  }

  /** ENT-16247 — short card summary sent as the feedback assistantResponse. */
  get feedbackSummary(): string {
    return `Rail ${this.card.from}→${this.card.to} ${this.card.departTime}-${this.card.arriveTime}, from ${this.card.fromPrice} ${this.card.currency}`;
  }

  get changesLabel(): string {
    if (this.card.changes === 0) {
      return $localize`:@@chatRailDirect:Direct`;
    }
    if (this.card.changes === 1) {
      return $localize`:@@chatRailOneChange:1 Change`;
    }
    return $localize`:@@chatRailManyChanges:${this.card.changes}:changes:+ Changes`;
  }

  get formattedPrice(): string {
    return this.formatCurrency(this.card.fromPrice, this.card.currency);
  }

  get cheapestClass(): string {
    const tickets = this.card.fares?.singleTickets ?? this.card.fares?.returnTickets;
    return tickets?.[0]?.classOfService || '';
  }

  /** Cheapest fare for this card — drives the collapsed "Singles from" headline rate. */
  private get headlineTicket(): ChatRailFareTicket | undefined {
    return this.card.fares?.singleTickets?.[0] ?? this.card.fares?.returnTickets?.[0];
  }

  get isHeadlineSplit(): boolean {
    return !!this.headlineTicket?.isSplit;
  }

  get headlineSavings(): string {
    return this.headlineTicket?.savings || '';
  }

  get headlineCurrency(): string {
    return this.headlineTicket?.currency || this.card.currency;
  }

  get singleFareLabel(): string {
    const name = this.card.fares?.singleTickets?.[0]?.type;
    if (name) {
      return name;
    }
    return this.isReturn
      ? $localize`:@@chatRailInboundSingle:Inbound Single`
      : $localize`:@@chatRailOutboundSingle:Outbound Single`;
  }

  get returnFareLabel(): string {
    return this.card.fares?.returnTickets?.[0]?.type ?? $localize`:@@chatRailReturnFare:Return`;
  }

  get isBlocked(): boolean {
    return this.card.policyStatus === 'UNAVAILABLE';
  }

  private resolveOperatorBadgeClass(): string {
    const op = (this.card.operator || '').toLowerCase();
    if (op.includes('sncf')) return 'rail-card__operator-badge--sncf';
    if (op.includes('thalys')) return 'rail-card__operator-badge--thalys';
    if (op.includes('db') || op.includes('deutsche bahn')) return 'rail-card__operator-badge--db';
    if (op.includes('eurostar')) return 'rail-card__operator-badge--eurostar';
    if (op.includes('ice')) return 'rail-card__operator-badge--db';
    if (op.includes('tgv')) return 'rail-card__operator-badge--sncf';
    return 'rail-card__operator-badge--default';
  }

  get policyClass(): string {
    return policyStatusClass(this.card.policyStatus);
  }

  get policyLabel(): string {
    return policyStatusLabel(this.policyClass);
  }

  /** Headline fare's policy fields, shaped for `app-warning-tags` — the same
   *  indicator/tooltip component the results page uses, so the chat card and
   *  results page render identically instead of a bespoke text box. */
  get headlineWarningTicket(): RuleActionExtension {
    return {
      unavailable: !!this.card.unavailable,
      unavailableMessage: this.card.unavailableMessage || '',
      requiresReasonKeys: this.card.requiresReasonKeys || [],
      requiresReasonMessages: this.card.requiresReasonMessages || []
    };
  }

  /** Per-fare policy fields for the expanded ticket list, shaped for
   *  `app-warning-tags` — a fare option can be out of policy independently of
   *  the headline card's cheapest fare. */
  warningTicketFor(ticket: ChatRailFareTicket): RuleActionExtension {
    return {
      unavailable: !!ticket.unavailable,
      unavailableMessage: ticket.unavailableMessage || '',
      requiresReasonKeys: ticket.requiresReasonKeys || [],
      requiresReasonMessages: ticket.requiresReasonMessages || []
    };
  }

  toggleExpand(): void {
    if (this.isBlocked) {
      return;
    }
    this.isExpanded = !this.isExpanded;
    if (!this.isExpanded) {
      this.expandedSection = null;
    }
  }

  onCardKey(event: Event): void {
    event.preventDefault();
    this.toggleExpand();
  }

  toggleSection(section: 'single' | 'return', event: Event): void {
    event.stopPropagation();
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  toggleReturnOption(index: number, event: Event): void {
    event.stopPropagation();
    this.expandedReturnIndex = this.expandedReturnIndex === index ? null : index;
  }

  closeSection(event: Event): void {
    event.stopPropagation();
    this.expandedSection = null;
  }

  onViewConditions(ticket: ChatRailFareTicket, event: Event): void {
    event.stopPropagation();
    this.resultAwareService.openRailFareDetails(ticket);
  }

  /**
   * ENT-16311 - true while a travelcard card is waiting for an answer. That
   * card is parked inside an add-to-basket, so a second fare click would start
   * a second one and orphan the first - which leaves the "requires reason"
   * prompts suppressed app-wide (see withReasonPromptsSuppressed).
   */
  get awaitingTravelcardChoice(): boolean {
    return this.chatbotService.pendingRailTravelcards.length > 0;
  }

  onSelectFare(ticket: ChatRailFareTicket, event: Event): void {
    event.stopPropagation();
    if (ticket.unavailable || this.awaitingTravelcardChoice) return;
    this.resultAwareService.selectRailFare(ticket).catch((error) => {
      console.error('[ChatRailCard] Rail fare selection failed:', error);
    });
  }

  // ENT-18234 — the second leg. Both calls go to the same service the first click
  // went to; the outbound fare it parked is what turns them into one basket add.
  onSelectReturnFare(ticket: ChatRailFareTicket, event: Event): void {
    event.stopPropagation();
    if (ticket.unavailable || this.awaitingTravelcardChoice) return;
    this.resultAwareService.selectRailFare(ticket).catch((error) => {
      console.error('[ChatRailCard] Return rail fare selection failed:', error);
    });
  }

  onSelectReturnTrain(option: ChatRailReturnOption, event: Event): void {
    event.stopPropagation();
    if (this.awaitingTravelcardChoice) return;
    this.resultAwareService.selectRailReturnTrain(option.index, option.fareHash).catch((error) => {
      console.error('[ChatRailCard] Return rail train selection failed:', error);
    });
  }

  /** The widget pins the message list to the bottom whenever a message arrives,
   *  which can push this card above the fold, so bring the options back after it. */
  private scrollReturnPromptIntoView(): void {
    setTimeout(() => {
      this.host.nativeElement
        .querySelector('.rail-card__return-prompt')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }

  // ENT-18234 - the station wall clock, taken straight out of the ISO string the
  // way ResultAwareService.formatLocalTime does. The date pipe would convert to the
  // viewer's own timezone and disagree with every other rail time in the chat.
  formatStationTime(isoString: string | undefined): string {
    const match = (isoString || '').match(/T(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '';
  }

  formatCurrency(amount: number | undefined, currency = 'GBP'): string {
    return formatCardCurrency(amount, currency);
  }
}
